import { sparkToast } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { TaskType } from '@/shared/domain/taskTypes';
import { isEmptyGenerateWithAi } from '@/features/datasets/utils/emptyResponsesHandling';
import { isEmptyTrajectoryGenerateWithAi } from '@/features/datasets/utils/emptyTrajectoriesHandling';
import { formatApiErrorDetail } from '@/shared/utils/formatApiErrorDetail';
import {
    applyChildProgressToCoalescedParentMap,
    initCloneProgressItem,
    librarySourceBaseForCloneGroup,
    markTwoPhaseFailed,
    predictedCloneNameForUsecase,
} from '@/features/datasets/utils/uploadProgress';

/**
 * Clone a library dataset/benchmark onto a usecase (and siblings when in a
 * multi-objective sub-group). Shared by FromLibraryTab and the Data Collection
 * panel library-pick path.
 */
export function cloneLibraryDatasetToTask({
    datasetName,
    isBenchmark,
    columnMappings,
    currentUsecase,
    subGroup = null,
    setProgressStatus,
    updateUsecase,
    registerUploadGroup,
    clearCancelledUploadSuppression,
    refreshUsecases,
    onComplete,
}) {
    if (!currentUsecase) return Promise.resolve();

    const buildPayload = (targetUsecase) => {
        const payload = {
            client_id: currentUsecase.client_id,
            source_dataset_name: datasetName,
            target_usecase_id: targetUsecase._id,
            is_benchmark: isBenchmark,
            task_type: targetUsecase.task_type,
        };
        if (columnMappings) {
            if (columnMappings.example_column_name) {
                payload.example_column_name = columnMappings.example_column_name;
            }
            if (columnMappings.label_column_name) {
                payload.label_column_name = columnMappings.label_column_name;
            }
            if (columnMappings.relations_column_name) {
                payload.relations_column_name = columnMappings.relations_column_name;
            }
            if (columnMappings.label_mappings) {
                payload.label_mappings = JSON.stringify(columnMappings.label_mappings);
            }
            if (columnMappings.relations_mappings) {
                payload.relations_mappings = JSON.stringify(columnMappings.relations_mappings);
            }
            if (columnMappings.classification_labels_delimiter) {
                payload.classification_labels_delimiter =
                    columnMappings.classification_labels_delimiter;
            }
            if (columnMappings.empty_multilabel_handling) {
                payload.empty_multilabel_handling = columnMappings.empty_multilabel_handling;
            }
            if (columnMappings.data_creation_type) {
                payload.data_creation_type = columnMappings.data_creation_type;
            }
            if (columnMappings.import_without_labels) {
                payload.import_without_labels = true;
            }
            if (columnMappings.update_rejection_label) {
                payload.update_rejection_label = true;
                payload.rejection_label_name = columnMappings.rejection_label_name || null;
            }
            if (columnMappings.defer_label_generation) {
                payload.defer_label_generation = true;
            }
            // Agentic tasks only; both stay absent otherwise.
            if (columnMappings.generate_trajectories) {
                payload.generate_trajectories = true;
            }
            if (columnMappings.empty_trajectory_handling) {
                payload.empty_trajectory_handling = columnMappings.empty_trajectory_handling;
            }
        }
        return payload;
    };

    const attachPredictedName = (uc, predicted) => {
        if (isBenchmark) {
            const b = Array.isArray(uc.benchmarks) ? [...uc.benchmarks] : [];
            if (!b.includes(predicted)) {
                updateUsecase?.(uc._id, { benchmarks: [...b, predicted] });
            }
        } else {
            const d = Array.isArray(uc.datasets) ? [...uc.datasets] : [];
            if (!d.includes(predicted)) {
                updateUsecase?.(uc._id, { datasets: [...d, predicted] });
            }
        }
    };

    const revertPredictedName = (uc, predicted) => {
        if (!uc?._id || !predicted) return;
        if (isBenchmark) {
            const b = Array.isArray(uc.benchmarks) ? uc.benchmarks : [];
            updateUsecase?.(uc._id, { benchmarks: b.filter((n) => n !== predicted) });
        } else {
            const d = Array.isArray(uc.datasets) ? uc.datasets : [];
            updateUsecase?.(uc._id, { datasets: d.filter((n) => n !== predicted) });
        }
    };

    const resolveProgressKey = (predicted, serverCloneName) => {
        const cloneName = serverCloneName || predicted;
        if (cloneName !== predicted) {
            setProgressStatus((prev) => {
                const existing = prev?.[predicted];
                if (!existing) return prev;
                const next = { ...prev };
                delete next[predicted];
                next[cloneName] = {
                    ...existing,
                    datasetName: existing.datasetName || datasetName,
                };
                return next;
            });
            const field = isBenchmark ? 'benchmarks' : 'datasets';
            const current = Array.isArray(currentUsecase[field]) ? currentUsecase[field] : [];
            updateUsecase?.(currentUsecase._id, {
                [field]: [...current.filter((n) => n !== predicted && n !== cloneName), cloneName],
            });
        }
        return cloneName;
    };

    const usecases = (subGroup?.usecases || []).filter((u) => u && u._id);
    const isMultiObjective = usecases.length > 1;
    const isEmptyGenerateWithAiChoice = isEmptyGenerateWithAi(
        columnMappings?.empty_multilabel_handling
    );
    const isAiLabeling = Boolean(
        columnMappings &&
            (isEmptyGenerateWithAiChoice ||
                (!columnMappings.label_column_name &&
                    ((columnMappings.label_mappings &&
                        Object.keys(columnMappings.label_mappings).length > 0) ||
                        columnMappings.generate_responses_with_ai ||
                        currentUsecase?.task_type === TaskType.summarization ||
                        currentUsecase?.task_type === TaskType.contentGeneration)))
    );
    // Agentic TRAJECTORY generation rides the same deferred/armed progress
    // card as LABEL_ONLY, labelled "Generating trajectories".
    const isTrajectoryGeneration = Boolean(
        columnMappings &&
            (columnMappings.generate_trajectories ||
                isEmptyTrajectoryGenerateWithAi(columnMappings.empty_trajectory_handling))
    );
    const isGenerationClone = isAiLabeling || isTrajectoryGeneration;
    const deferLabelGeneration = Boolean(
        isGenerationClone && columnMappings?.defer_label_generation
    );

    if (!isMultiObjective) {
        return new Promise((resolve) => {
            const groupKey = librarySourceBaseForCloneGroup(datasetName);
            const predicted = predictedCloneNameForUsecase(groupKey, currentUsecase._id);
            const payload = buildPayload(currentUsecase);

            clearCancelledUploadSuppression?.(predicted);
            initCloneProgressItem({
                setProgressStatus,
                key: predicted,
                datasetName,
                usecaseId: currentUsecase._id,
                clientId: currentUsecase.client_id,
                isBenchmark,
                message: `Cloning '${datasetName}'`,
                affectedUsecaseIds: [currentUsecase._id],
                isLabelGeneration: isGenerationClone,
                isTrajectoryGeneration,
                deferLabelGeneration,
            });
            attachPredictedName(currentUsecase, predicted);

            data_from_server_post(
                'clone-dataset-to-task',
                payload,
                (response) => {
                    if (response?.status === 'cloning' || response?.status === 'attached') {
                        const cloneName = resolveProgressKey(predicted, response.clone_name);
                        const done = response.status === 'attached';
                        setProgressStatus((prev) => ({
                            ...prev,
                            [cloneName]: {
                                ...(prev?.[cloneName] || {}),
                                progress: done ? 100 : 50,
                                uploadProgress: done ? 100 : undefined,
                                phase: done ? 'completed' : 'processing',
                                isCompleted: done,
                                isError: false,
                                message: done ? 'Processing complete' : `Cloning '${datasetName}'`,
                                datasetName,
                                clientId: currentUsecase.client_id,
                                usecaseId: currentUsecase._id,
                                isBenchmark,
                                affectedUsecaseIds: [currentUsecase._id],
                                isLabelGeneration: isGenerationClone,
                                isTrajectoryGeneration,
                                deferLabelGeneration,
                                details: done ? null : undefined,
                            },
                        }));
                        refreshUsecases?.();
                        onComplete?.();
                        resolve();
                    } else {
                        const cloneError = formatApiErrorDetail(response?.detail) || 'Clone failed';
                        sparkToast.error(cloneError);
                        revertPredictedName(currentUsecase, predicted);
                        markTwoPhaseFailed({
                            setProgressStatus,
                            key: predicted,
                            errorMessage: cloneError,
                        });
                        refreshUsecases?.();
                        resolve();
                    }
                },
                (error) => {
                    const message =
                        formatApiErrorDetail(error?.detail) ||
                        (typeof error === 'string' ? error : error?.message) ||
                        'Failed to clone dataset';
                    sparkToast.error(message);
                    revertPredictedName(currentUsecase, predicted);
                    markTwoPhaseFailed({
                        setProgressStatus,
                        key: predicted,
                        errorMessage: message,
                    });
                    refreshUsecases?.();
                    resolve();
                }
            );
        });
    }

    return new Promise((resolve) => {
        const groupKey = librarySourceBaseForCloneGroup(datasetName);
        const childCloneNames = usecases.map((uc) =>
            predictedCloneNameForUsecase(groupKey, uc._id)
        );
        const childCount = childCloneNames.length;
        const affectedIds = usecases.map((u) => u._id);

        const applyMultiMerge = (
            nextIn,
            childCloneName,
            childProgress,
            childIsError
        ) =>
            applyChildProgressToCoalescedParentMap(nextIn, {
                parentKey: groupKey,
                childCount,
                childCloneName,
                childProgress,
                childIsError,
                preserve: (existing) => ({
                    datasetName: existing.datasetName || datasetName,
                    clientId: existing.clientId ?? currentUsecase.client_id,
                    usecaseId: existing.usecaseId ?? currentUsecase._id,
                    isBenchmark: existing.isBenchmark ?? isBenchmark,
                    affectedUsecaseIds: existing.affectedUsecaseIds || affectedIds,
                }),
            });

        clearCancelledUploadSuppression?.(groupKey);
        registerUploadGroup?.(groupKey, childCloneNames, {
            datasetDisplayName: datasetName,
            affectedUsecaseIds: affectedIds,
            clientId: currentUsecase.client_id,
            isBenchmark,
            usecaseIdPrimary: currentUsecase._id,
        });
        initCloneProgressItem({
            setProgressStatus,
            key: groupKey,
            datasetName,
            usecaseId: currentUsecase._id,
            clientId: currentUsecase.client_id,
            isBenchmark,
            message: `Cloning '${datasetName}'`,
            affectedUsecaseIds: affectedIds,
        });

        for (const uc of usecases) {
            attachPredictedName(uc, predictedCloneNameForUsecase(groupKey, uc._id));
        }

        Promise.all(
            usecases.map(
                (uc) =>
                    new Promise((res) => {
                        data_from_server_post(
                            'clone-dataset-to-task',
                            buildPayload(uc),
                            (response) => res({ uc, response, transportOk: true }),
                            () => res({ uc, response: null, transportOk: false })
                        );
                    })
            )
        ).then((rows) => {
            let hadError = false;
            setProgressStatus((prev) => {
                let next = { ...prev };
                for (const row of rows) {
                    const predictedClone = predictedCloneNameForUsecase(groupKey, row.uc._id);
                    if (!row.transportOk) {
                        hadError = true;
                        next = applyMultiMerge(next, predictedClone, 0, true);
                        continue;
                    }
                    if (row.response?.status === 'cloning') {
                        next = applyMultiMerge(next, predictedClone, 0, false);
                    } else if (row.response?.status === 'attached') {
                        next = applyMultiMerge(next, predictedClone, 100, false);
                    } else {
                        hadError = true;
                        next = applyMultiMerge(next, predictedClone, 0, true);
                    }
                }
                return next;
            });
            if (hadError) {
                const bad = rows.find(
                    (r) =>
                        !r.transportOk ||
                        (r.response &&
                            r.response.status !== 'cloning' &&
                            r.response.status !== 'attached')
                );
                sparkToast.error(
                    bad?.response?.detail ||
                        (!bad?.transportOk
                            ? 'Failed to clone dataset'
                            : 'Clone failed for one or more tasks')
                );
                for (const row of rows) {
                    const ok =
                        row.transportOk &&
                        (row.response?.status === 'cloning' ||
                            row.response?.status === 'attached');
                    if (!ok) {
                        revertPredictedName(
                            row.uc,
                            predictedCloneNameForUsecase(groupKey, row.uc._id)
                        );
                    }
                }
                refreshUsecases?.();
                resolve();
                return;
            }
            refreshUsecases?.();
            onComplete?.();
            resolve();
        });
    });
}
